/**
 * The card on the desk: who, what, and the act that resolves it.
 *
 * The frame is unchanged from the 18 September canon — the head with the person and the position
 * chip, a body that scrolls, the action bar pinned at the foot. The BODY is KANON "Karty zadań"
 * (owner, 24 September 2026, `docs/design/karty-zadan-2026-09-24/catalogue6.js`): the queue
 * feature's `TaskHeading` (the kind, its line, its one figure) and `TaskBody` at its `desk` density
 * — the same content the Pulpit and the client's Przegląd draw — with every write the kind allows
 * made on the spot (see `features/attention-queue`'s `lib/taskWrites`).
 *
 * ── The action bar keeps only what the body does not already do ─────────────────────────────
 * The note is written in the body, the plan's and the goal's writes stand in the body. What is left
 * for the bar is „Oznacz jako przejrzane" (a write with no home in the body), the chat's „Napisz DM",
 * the clip's way into the one player on the client's card (the body draws its poster and never plays
 * it — view ADR of 23 September 2026) — and beside them „Napisz DM" and „Otwórz klienta", on every card
 * with a client. `↵` presses the first of them. No primary is refused: "Gotowe" and "Później" stand
 * under the card (`DecisionBar`) and work on every one (R4).
 *
 * ── „Napisz DM" and „Odpisz" ────────────────────────────────────────────────────────────────
 * Both open the conversation with the card's client in the chat panel over this desk (chat canon,
 * boards P1–P8), so the deck stays where it is; the panel's „Otwórz w Czacie" is the way to the full
 * screen. Every act here is a small line glyph beside its word; the word is the name.
 *
 * ── "Oznacz jako przejrzane" is NOT "Gotowe" ────────────────────────────────────────────────
 * `ReviewedAt` is visible to the client (`intake-survey` R31); a queue response is private to the
 * responder (R4). Both controls are drawn, and never one under the other's name.
 */

import { Link } from '@tanstack/react-router';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ActGlyphMark,
  actionGlyph,
  clipDestination,
  headingStandsInBody,
  primaryNaming,
  primaryStandsInFoot,
  sessionDestination,
  surveyDestination,
  surveyRead,
  TaskBody,
  TaskHeading,
  taskLabel,
  useTaskContent,
  type TaskSlots,
} from '~/features/attention-queue';
import { sessionOpenerKey } from '~/shared/lib/sessionOpener';
import { useOverflowEdge } from '~/shared/lib/useOverflowEdge';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';

import { TASK_PRIMARY_ATTRIBUTE } from '../lib/taskMarks';

import type { DeckCard } from '../model/taskDeck';
import styles from './taskCard.module.css';

interface TaskCardProps {
  readonly card: DeckCard;
  /** Where the card stands in the whole deck, one-based, and how big the deck is. */
  readonly position: number;
  readonly total: number;
  /** When the signal happened, worded. `''` where the wire carried no stamp. */
  readonly when: string;
  /** The screen's clock for the sitting — the day the weeks and the months are drawn around. */
  readonly now: Date;
  /** The writes and the two other features' nodes the body takes. See `TasksScreen`. */
  readonly slots: TaskSlots;
  readonly reducedMotion: boolean;
  /** This card's implicit close is in flight: the primary says so and takes no second press. */
  readonly primarySending: boolean;
  /** The server refused it. The card says so beside the control that was pressed. */
  readonly primaryRefused: boolean;
  /** The primary was pressed. What it does is the screen's, because two of the kinds write. */
  readonly onPrimary: () => void;
  /** „Zobacz sesję”: lay the session panel over the desk. */
  readonly onOpenSession: (workoutExecutionId: number) => void;
  /** The clip card's reply field, which „Zostaw notatkę" focuses rather than leaving the desk. */
  readonly clipField: RefObject<HTMLInputElement | null>;
  /** Open the conversation with this card's client in the chat panel. */
  readonly onOpenChat: (customerId: number) => void;
}

export function TaskCard({
  card,
  position,
  total,
  when,
  now,
  slots,
  reducedMotion,
  primarySending,
  primaryRefused,
  onPrimary,
  onOpenSession,
  clipField,
  onOpenChat,
}: TaskCardProps) {
  const { t } = useTranslation();
  const item = card.item;
  const action = item.action;
  const person = taskLabel(item, t);
  const content = useTaskContent(item, now);
  /** The bar's own primary, or `null` where the body already does the act. */
  const barPrimary = primaryStandsInFoot(action, 'desk');
  // On a short window the body scrolls inside the card, and an overlay scrollbar is invisible until
  // the trainer is already scrolling: the far edge fades while anything is hidden past it.
  // Destructured, as `SurveyStream` does: the compiler's ref rule reads `edge.ref` as a ref read.
  const { ref: bodyRef, hasMore: bodyHasMore } = useOverflowEdge<HTMLDivElement>('block');
  /** The session this card names, where the panel can open it (`model/sessionLink`). */
  const session = sessionDestination(item);
  /** The survey this card leads into — a submitted one to read, or the check-in a low reading came from. */
  const survey = surveyRead(item);
  /**
   * „Napisz DM" beside every card about a client, except where the primary already IS the chat — and
   * never on the trainer's own profile, which nobody can write to (`messageable`).
   */
  const writes = item.messageable && action.kind !== 'openChat';

  return (
    <article className={styles.card} aria-label={person}>
      <div className={styles.head}>
        <AvatarChip
          className={styles.avatar}
          initials={item.initials}
          avatarUrl={item.avatarUrl}
        />
        <div className={styles.headText}>
          <h2 className={styles.name}>{item.fullName}</h2>
          <p className={styles.when}>
            {when === '' ? '' : `${when} · `}
            {t(
              // A record waits for nothing: it is a moment to write to the client (R24).
              item.reason === 'prAchieved'
                ? 'web:tasks.card.congratulate'
                : card.category === 'Feedback'
                  ? 'web:tasks.card.waitingWord'
                  : 'web:tasks.card.waitingMove',
            )}
          </p>
        </div>
        <span className={styles.position}>
          {t('web:tasks.card.position', { index: position, total })}
        </span>
      </div>

      <div ref={bodyRef} className={`${String(styles.body)} ${bodyHasMore ? String(styles.bodyMore) : ''}`}>
        {/* A record's heading stands inside its hero, beside the photograph (`RecordBlock`). */}
        {headingStandsInBody(item.reason) ? null : <TaskHeading item={item} content={content} />}
        <TaskBody
          item={item}
          density="desk"
          content={content}
          now={now}
          slots={slots}
          reducedMotion={reducedMotion}
        />
      </div>

      <div className={styles.cta}>
        {barPrimary ? primary() : null}
        {/* The reads: the whole survey on the client's Ankiety, the whole session in the panel laid
          * over this desk (owner, 25 September 2026). */}
        {survey !== null && item.subjectCustomerId !== null ? (
          <Link
            className={`${ghost.ghost} ${styles.secondary}`}
            {...surveyDestination(item.subjectCustomerId, survey.surveyAssignmentId)}
            aria-label={t('web:home.action.forTask', { action: t(survey.labelKey), task: person })}
          >
            <ActGlyphMark glyph="survey" />
            {t(survey.labelKey)}
          </Link>
        ) : null}
        {session === null ? null : (
          <button
            type="button"
            className={`${ghost.ghost} ${styles.secondary}`}
            data-session-opener={sessionOpenerKey(session.workoutExecutionId, 'read')}
            aria-label={t('web:home.action.forTask', { action: t('chat.rich.viewSession'), task: person })}
            onClick={() => {
              onOpenSession(session.workoutExecutionId);
            }}
          >
            <ActGlyphMark glyph="session" />
            {t('chat.rich.viewSession')}
          </button>
        )}
        {writes && item.subjectCustomerId !== null ? (
          <button
            className={`${ghost.ghost} ${styles.secondary}`}
            type="button"
            aria-label={t('web:home.action.forTask', { action: t('web:home.action.writeDm'), task: person })}
            onClick={() => {
              if (item.subjectCustomerId !== null) onOpenChat(item.subjectCustomerId);
            }}
          >
            <ActGlyphMark glyph="write" />
            {t('web:home.action.writeDm')}
          </button>
        ) : null}
        {/* The refusal beside the control that was pressed. This app has no toasts, and the live
          * region alone is not where the trainer is looking a moment after pressing a button. */}
        {primaryRefused ? <p className={styles.refusal}>{t('web:home.live.quickFailed')}</p> : null}
        {primaryRefused ? null : <span className={styles.spacer} />}
        {card.handled === null ? null : (
          <span className={styles.state}>
            {t(card.handled === 'snoozed' ? 'web:tasks.card.stateSnoozed' : 'web:tasks.card.stateDone')}
          </span>
        )}
        {/* The way out, for the trainer who wants the whole module. The invitation's invitee is not
          * a client yet (R10), so its way out is the roster, where the invitation lives; a public
          * form lead is not a client either, and its module is the leads inbox — unless the item named
          * no lead, when the primary already IS the inbox. */}
        {action.kind === 'openLead' ? (
          action.leadId === null ? null : (
            <Link
              className={`${ghost.ghost} ${styles.secondary}`}
              to="/klienci/zgloszenia"
              aria-label={t('web:home.action.forTask', { action: t('web:publicForm.leads.button'), task: person })}
            >
              <ActGlyphMark glyph="survey" />
              {t('web:publicForm.leads.button')}
            </Link>
          )
        ) : item.subjectCustomerId === null ? (
          <Link
            className={`${ghost.ghost} ${styles.secondary}`}
            to="/klienci"
            {...(barPrimary ? {} : { [TASK_PRIMARY_ATTRIBUTE]: '' })}
          >
            <ActGlyphMark glyph="invitation" />
            {t('web:home.action.openInvitation')}
          </Link>
        ) : (
          <Link
            className={`${ghost.ghost} ${styles.secondary}`}
            to="/klienci/$clientId"
            params={{ clientId: String(item.subjectCustomerId) }}
            aria-label={t('web:tasks.card.openClientFor', { name: person })}
            {...(barPrimary ? {} : { [TASK_PRIMARY_ATTRIBUTE]: '' })}
          >
            <ActGlyphMark glyph="client" />
            {t('web:tasks.card.openClient')}
          </Link>
        )}
      </div>
    </article>
  );

  function primary() {
    const label = t(action.labelKey);
    const { name, hint } = primaryNaming(item, label, t);
    // What `↵` reaches. Written here because this is the file that knows which element the primary
    // actually is.
    const mark = { [TASK_PRIMARY_ATTRIBUTE]: '' };

    switch (action.kind) {
      case 'markReviewed':
        return (
          <button
            className={`${ink.ink} ${styles.primary}`}
            type="button"
            aria-label={name}
            // `aria-busy`, never `disabled`: a control that disables itself under the trainer's own
            // focus drops the keyboard on `<body>` and the next Tab restarts at the skip link. The
            // second press is refused in `useImplicitResolution`, where the gate lives.
            aria-busy={primarySending ? 'true' : undefined}
            onClick={onPrimary}
            {...mark}
          >
            <ActGlyphMark glyph={actionGlyph(action)} />
            {label}
          </button>
        );
      case 'openChat': {
        const customerId = item.subjectCustomerId;
        if (customerId === null) {
          return (
            <Link className={`${ink.ink} ${styles.primary}`} to="/czat" aria-label={name} title={hint} {...mark}>
              <ActGlyphMark glyph={actionGlyph(action)} />
              {label}
            </Link>
          );
        }
        return (
          <button
            className={`${ink.ink} ${styles.primary}`}
            type="button"
            aria-label={name}
            title={hint}
            onClick={() => {
              onOpenChat(customerId);
            }}
            {...mark}
          >
            <ActGlyphMark glyph={actionGlyph(action)} />
            {label}
          </button>
        );
      }
      case 'openClip':
        // A question under a clip is answered on the card itself: the reply field stands beside the
        // film (owner, 25 September 2026), so „Zostaw notatkę" takes the trainer there.
        if (item.detail.kind === 'clipComment' && item.detail.clipId !== null) {
          return (
            <button
              className={`${ink.ink} ${styles.primary}`}
              type="button"
              aria-label={name}
              onClick={() => {
                clipField.current?.focus();
              }}
              {...mark}
            >
              <ActGlyphMark glyph={actionGlyph(action)} />
              {label}
            </button>
          );
        }
        return (
          <Link className={`${ink.ink} ${styles.primary}`} {...clipDestination(action)} aria-label={name} {...mark}>
            <ActGlyphMark glyph={actionGlyph(action)} />
            {label}
          </Link>
        );
      case 'openLead':
        return action.leadId === null ? (
          <Link className={`${ink.ink} ${styles.primary}`} to="/klienci/zgloszenia" aria-label={name} {...mark}>
            <ActGlyphMark glyph={actionGlyph(action)} />
            {label}
          </Link>
        ) : (
          <Link
            className={`${ink.ink} ${styles.primary}`}
            to="/klienci/zgloszenia/$leadId"
            params={{ leadId: String(action.leadId) }}
            aria-label={name}
            {...mark}
          >
            <ActGlyphMark glyph={actionGlyph(action)} />
            {label}
          </Link>
        );
      default:
        return null;
    }
  }
}
